// Motion layer: Lenis smooth scrolling + GSAP animations.
//
// Everything here is progressive enhancement. The markup is fully visible
// without JS; CSS (components/_motion.scss) hides the animated elements
// only while <html> carries `has-js`, and this file reveals them. With
// prefers-reduced-motion the flag is dropped and nothing animates.
//
// Hooks in the markup:
//   [data-intro]            page-load stagger (hero copy, page heads)
//   [data-intro-title]      the h1 — split into words and masked in
//   [data-intro-card]       the signup card that rises beside the hero
//   [data-stamp] [data-drop] status-page artwork
//   [data-reveal]           fade/rise once when scrolled into view
//   [data-reveal-stagger]   same, children one after another
//   [data-parallax-bg]      graph-paper background drifts with scroll
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import Lenis from "lenis";

gsap.registerPlugin(ScrollTrigger, SplitText);

const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

export function initMotion() {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Tells the inline failsafe in <head> the bundle is alive.
  window.__wpsReady = true;

  if (reduceMotion) {
    root.classList.remove("has-js");
    return;
  }

  smoothScroll();
  intro();
  reveals();
  parallax();
  sealSpin();

  // Fonts and images shift the layout the triggers were measured against.
  window.addEventListener("load", () => ScrollTrigger.refresh());
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => ScrollTrigger.refresh());
  }
}

// ─── Smooth scroll ────────────────────────────────────────────────────────────

function smoothScroll() {
  const lenis = new Lenis({
    lerp: 0.1,
    wheelMultiplier: 1,
    anchors: true, // same-page #links glide instead of jumping
  });

  // One clock for everything: GSAP's ticker drives Lenis, Lenis feeds
  // ScrollTrigger.
  lenis.on("scroll", ScrollTrigger.update);
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);

  // Opened as /#subscribe from another page: the browser already jumped;
  // nothing to do. Opened with a hash on a page that has just revealed
  // content: re-sync Lenis to the native position.
  window.addEventListener("load", () => lenis.resize());

  return lenis;
}

// ─── Page-load sequence ───────────────────────────────────────────────────────

function intro() {
  const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

  const header = document.querySelector(".js-site-header");
  if (header) {
    tl.fromTo(header, { autoAlpha: 0, y: -28 }, { autoAlpha: 1, y: 0, duration: 0.7, clearProps: "transform" }, 0);
  }

  // Heading: split into words, each rising out of its own mask.
  $$("[data-intro-title]").forEach((title) => {
    gsap.set(title, { opacity: 1 });
    const split = SplitText.create(title, { type: "words", mask: "words", wordsClass: "split-word" });
    tl.from(split.words, { yPercent: 115, duration: 0.9, stagger: 0.08, ease: "power4.out" }, 0.15);
  });

  const items = $$("[data-intro]");
  if (items.length) {
    tl.fromTo(
      items,
      { autoAlpha: 0, y: 26 },
      { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.1, clearProps: "transform" },
      0.3
    );
  }

  $$("[data-intro-card]").forEach((card) => {
    tl.fromTo(
      card,
      { autoAlpha: 0, y: 44, scale: 0.97 },
      { autoAlpha: 1, y: 0, scale: 1, duration: 0.9, clearProps: "transform" },
      0.45
    );
  });

  $$("[data-drop]").forEach((art) => {
    tl.fromTo(art, { autoAlpha: 0, y: -26 }, { autoAlpha: 1, y: 0, duration: 0.9, ease: "bounce.out" }, 0.2);
  });

  // The stamp lands on the page and settles at its resting tilt (-4deg).
  $$("[data-stamp]").forEach((stamp) => {
    tl.fromTo(
      stamp,
      { autoAlpha: 0, scale: 1.35, rotation: -9 },
      { autoAlpha: 1, scale: 1, rotation: -4, duration: 0.7, ease: "back.out(2.2)" },
      0.2
    );
  });
}

// ─── Scroll reveals ───────────────────────────────────────────────────────────

function reveals() {
  $$("[data-reveal]").forEach((el) => {
    gsap.fromTo(
      el,
      { autoAlpha: 0, y: 40 },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.9,
        ease: "power3.out",
        clearProps: "transform",
        scrollTrigger: { trigger: el, start: "top 88%", once: true },
      }
    );
  });

  $$("[data-reveal-stagger]").forEach((group) => {
    gsap.fromTo(
      Array.from(group.children),
      { autoAlpha: 0, y: 30 },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
        stagger: 0.1,
        clearProps: "transform",
        scrollTrigger: { trigger: group, start: "top 86%", once: true },
      }
    );
  });
}

// ─── Graph-paper drift ────────────────────────────────────────────────────────

function parallax() {
  $$("[data-parallax-bg]").forEach((section) => {
    gsap.fromTo(
      section,
      { backgroundPositionY: "0px" },
      {
        backgroundPositionY: "-120px",
        ease: "none",
        scrollTrigger: { trigger: section, start: "top bottom", end: "bottom top", scrub: true },
      }
    );
  });
}

// ─── Brand seal: one slow turn on hover ───────────────────────────────────────

function sealSpin() {
  const brand = document.querySelector(".brand");
  const seal = brand && brand.querySelector(".seal, .brand__logo");
  if (!seal) return;

  brand.addEventListener("mouseenter", () => {
    if (gsap.isTweening(seal)) return;
    gsap.to(seal, { rotation: "+=360", duration: 1.1, ease: "power2.inOut" });
  });
}
